import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { LayoutGrid, Table, ChevronDown, Filter, Search, X } from 'lucide-react'
import { usePipelineContext } from '../PipelineContext'

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SelectButton = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #F5F5F5;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  min-width: 120px;
  justify-content: space-between;

  &:hover {
    background-color: #EBEBEB;
  }

  svg:last-child {
    transition: transform 0.2s;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`

const Dropdown = styled.div<{ $width?: string }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: ${props => props.$width || '180px'};
  overflow: hidden;
`

const DropdownItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${props => props.$isActive ? '#0090D3' : '#333'};
  background-color: ${props => props.$isActive ? '#F0F9FF' : 'transparent'};

  &:hover {
    background-color: #F5F5F5;
  }
`

const FilterButton = styled.button<{ $hasFilters?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${props => props.$hasFilters ? '#0090D3' : 'transparent'};
  border: 1px solid ${props => props.$hasFilters ? '#0090D3' : '#0090D3'};
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${props => props.$hasFilters ? '#fff' : '#0090D3'};

  &:hover {
    background-color: ${props => props.$hasFilters ? '#007bb5' : '#E6F4FB'};
  }
`

const FilterDropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  width: 280px;
  max-height: 400px;
  overflow-y: auto;
`

const FilterSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #E0E0E0;

  &:last-child {
    border-bottom: none;
  }
`

const FilterSectionTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 0.75rem;
`

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #F5F5F5;
  border-radius: 0.5rem;

  input {
    border: none;
    background: transparent;
    outline: none;
    flex: 1;
    font-size: 0.875rem;
  }
`

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`

const UserItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`

const UserAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #0090D3;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
`

const TagItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  cursor: pointer;
`

const TagBadge = styled.span<{ $color?: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  background-color: ${props => props.$color || '#E0E0E0'};
  color: ${props => props.$color ? '#fff' : '#333'};
`

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const OrderLabel = styled.span`
  font-size: 0.875rem;
  color: #666;
`

interface ViewModeSelectorProps {
  value: 'board' | 'table'
  onChange: (value: 'board' | 'table') => void
  boardLabel?: string
  tableLabel?: string
}

export const ViewModeSelector: React.FC<ViewModeSelectorProps> = ({
  value,
  onChange,
  boardLabel = 'Board',
  tableLabel = 'Tabela',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <SelectWrapper ref={ref}>
      <SelectButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
        {value === 'board' ? <LayoutGrid size={16} /> : <Table size={16} />}
        {value === 'board' ? boardLabel : tableLabel}
        <ChevronDown size={16} />
      </SelectButton>
      {isOpen && (
        <Dropdown>
          <DropdownItem $isActive={value === 'board'} onClick={() => { onChange('board'); setIsOpen(false) }}>
            <LayoutGrid size={16} /> {boardLabel}
          </DropdownItem>
          <DropdownItem $isActive={value === 'table'} onClick={() => { onChange('table'); setIsOpen(false) }}>
            <Table size={16} /> {tableLabel}
          </DropdownItem>
        </Dropdown>
      )}
    </SelectWrapper>
  )
}

interface SortOption {
  value: string
  label: string
}

const sortOptions: SortOption[] = [
  { value: 'phase', label: 'Etapa' },
  { value: 'name_asc', label: 'Nome (crescente)' },
  { value: 'name_desc', label: 'Nome (decrescente)' },
  { value: 'recent', label: 'Mais recente' },
  { value: 'oldest', label: 'Menos recente' },
  { value: 'urgency', label: 'Urgência' },
  { value: 'deadline', label: 'Próximo de vencer' },
]

interface SortBySelectorProps {
  value: string
  onChange: (value: string) => void
}

export const SortBySelector: React.FC<SortBySelectorProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const currentLabel = sortOptions.find(o => o.value === value)?.label || 'Nome (crescente)'

  return (
    <SelectWrapper ref={ref}>
      <OrderLabel>Ordenar por</OrderLabel>
      <SelectButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen} style={{ marginLeft: '0.5rem' }}>
        {currentLabel}
        <ChevronDown size={16} />
      </SelectButton>
      {isOpen && (
        <Dropdown $width="200px">
          {sortOptions.map(option => (
            <DropdownItem 
              key={option.value} 
              $isActive={value === option.value}
              onClick={() => { onChange(option.value); setIsOpen(false) }}
            >
              {option.label}
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </SelectWrapper>
  )
}

interface FilterDropdownProps {
  users?: { id: number; name: string; picture?: string }[]
  tags?: { id: number; name: string; color?: string }[]
}

export const FilterDropdown: React.FC<FilterDropdownProps> = () => {
  const { filters, setFilters, availableUsers, availableTags } = usePipelineContext()
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const hasActiveFilters = !!(filters.keyword || filters.urgency || 
    filters.dueDate.notExpired || filters.dueDate.nearExpiry || filters.dueDate.expired ||
    filters.assignedUsers.length > 0 || filters.tags.length > 0)

  const toggleUser = (userId: number) => {
    setFilters(prev => ({
      ...prev,
      assignedUsers: prev.assignedUsers.includes(userId)
        ? prev.assignedUsers.filter(id => id !== userId)
        : [...prev.assignedUsers, userId]
    }))
  }

  const toggleTag = (tagId: number) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }))
  }

  console.debug('[FilterDropdown] Available users:', availableUsers)
  console.debug('[FilterDropdown] Available tags:', availableTags)

  return (
    <SelectWrapper ref={ref} style={{ position: 'relative' }}>
      <FilterButton onClick={() => setIsOpen(!isOpen)} $hasFilters={hasActiveFilters}>
        <Filter size={16} /> Filtrar
      </FilterButton>
      {isOpen && (
        <FilterDropdownContainer>
          <FilterSection>
            <FilterSectionTitle>Filtrar</FilterSectionTitle>
            <FilterSectionTitle style={{ fontWeight: 400, marginBottom: '0.5rem' }}>Palavra-chave</FilterSectionTitle>
            <SearchInput>
              <Search size={16} color="#999" />
              <input 
                type="text" 
                placeholder="Buscar" 
                value={filters.keyword}
                onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              />
              {filters.keyword && (
                <X 
                  size={16} 
                  color="#999" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => setFilters(prev => ({ ...prev, keyword: '' }))}
                />
              )}
            </SearchInput>
          </FilterSection>

          <FilterSection>
            <FilterSectionTitle>Urgência</FilterSectionTitle>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.urgency}
                onChange={(e) => setFilters(prev => ({ ...prev, urgency: e.target.checked }))}
              />
              Urgente
            </CheckboxItem>
          </FilterSection>

          <FilterSection>
            <FilterSectionTitle>Data de vencimento</FilterSectionTitle>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.notExpired}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: { ...prev.dueDate, notExpired: e.target.checked }
                }))}
              />
              Não vencido
            </CheckboxItem>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.nearExpiry}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: { ...prev.dueDate, nearExpiry: e.target.checked }
                }))}
              />
              Próximo ao vencimento
            </CheckboxItem>
            <CheckboxItem>
              <input 
                type="checkbox" 
                checked={filters.dueDate.expired}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  dueDate: { ...prev.dueDate, expired: e.target.checked }
                }))}
              />
              Vencido
            </CheckboxItem>
          </FilterSection>

          {availableUsers.length > 0 && (
            <FilterSection>
              <FilterSectionTitle>Atribuição</FilterSectionTitle>
              {availableUsers.map(user => (
                <UserItem key={user.id}>
                  <input 
                    type="checkbox" 
                    checked={filters.assignedUsers.includes(user.id)}
                    onChange={() => toggleUser(user.id)}
                  />
                  <UserAvatar>
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      user.name.charAt(0).toUpperCase()
                    )}
                  </UserAvatar>
                  {user.name}
                </UserItem>
              ))}
            </FilterSection>
          )}

          {availableTags.length > 0 && (
            <FilterSection>
              <FilterSectionTitle>Tags</FilterSectionTitle>
              {availableTags.map(tag => (
                <TagItem key={tag.id}>
                  <input 
                    type="checkbox" 
                    checked={filters.tags.includes(tag.id)}
                    onChange={() => toggleTag(tag.id)}
                  />
                  <TagBadge $color={tag.color}>{tag.name}</TagBadge>
                </TagItem>
              ))}
            </FilterSection>
          )}
        </FilterDropdownContainer>
      )}
    </SelectWrapper>
  )
}

export { ControlsContainer }
